/**
 * Vinduet vaktplanen leses gjennom — og det er ikke årgangens egne dager.
 *
 * ---------------------------------------------------------------------------------------------
 * ATLE, 1. SEPTEMBER: «Man kan heller ikke se vaktplan i dagene rundt festivalen, man må kunne
 * blake "uendelig" i dager og sette opp rundt arrangementet.»
 *
 * Han har rett, og feilen var hard: dagvelgeren ble bygget av `starts_on`–`ends_on`, så en riggvakt
 * 12. juli kunne lagres og ikke vises når festivalen åpnet den 15. En festival varer lenger enn
 * festivalen — rigg starter dager før, riving går dager etter, og riggvaktene er ofte flere enn
 * festivalvaktene og alltid de vanskeligste å bemanne. Se `docs/design/05-vaktplanlegging.md` §1.1.
 *
 * ---------------------------------------------------------------------------------------------
 * DAGER ER KALENDERDAGER, IKKE DØGN FRA ET TIDSPUNKT.
 *
 * Alt her regnes på `YYYY-MM-DD` gjennom UTC-midnatt, som er identiteten som gjør at en dag
 * overlever et rundtur uansett hvilken tidssone leseren står i. Hvilken dag en VAKT hører til er et
 * annet spørsmål, og det svares på i årgangens sone — se `dayOfInstant`.
 */

import { instantOf } from '@/lib/format';

/** How many days the strip shows at once. A week is what a plan is read in. */
export const WINDOW_DAYS = 7;

const DAY_MS = 86_400_000;

/**
 * `YYYY-MM-DD` plus whole days, on the calendar rather than on a clock that has offsets.
 *
 * EN DAG SOM IKKE ER EN DAG KOMMER UENDRET TILBAKE (N1, 20. september). `new Date(NaN).toISOString()`
 * kaster — det er den samme feilformen som veltet hurtiglegg, i en funksjon ingen ville lett i.
 */
export function addDays(day: string, days: number): string {
  const at = Date.parse(`${day}T00:00:00Z`);
  if (Number.isNaN(at)) return day;
  return new Date(at + days * DAY_MS).toISOString().slice(0, 10);
}

/** The `WINDOW_DAYS` days starting at `first`. */
export function windowDays(first: string, count: number = WINDOW_DAYS): string[] {
  return Array.from({ length: count }, (_, index) => addDays(first, index));
}

/**
 * Which calendar day an instant falls on, read on the ÅRGANG's clock rather than the reader's.
 *
 * Svaret er en NØKKEL og ikke en tekst noen leser, så en ugyldig dato blir den tomme nøkkelen:
 * vakta havner ikke på en dag, framfor at `Intl` kaster og hele vaktplanen forsvinner (N1).
 */
export function dayOfInstant(iso: string, timeZone: string): string {
  const instant = instantOf(iso);
  if (instant === null) return '';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant);
}

/**
 * FESTIVALDØGNET ET TIDSPUNKT HØRER TIL (klikkrunden K3 F10): 00:30 natt til lørdag er fredag.
 *
 * Tidslinja tegner hver dag fra `dayStartHour` til samme klokkeslett neste dag. Ble hendelsene
 * gruppert på kalenderdato, havnet Curfew 00:00 og et sett 00:00–01:00 under neste dato, FØR den
 * stripas start — og ble hoppet over uten et spor. Et tidspunkt før grensen hører til gårsdagen.
 */
export function festivalDayOfInstant(iso: string, timeZone: string, dayStartHour: number): string {
  const instant = instantOf(iso);
  if (instant === null) return '';
  const day = dayOfInstant(iso, timeZone);
  const hour = Number(
    new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', hourCycle: 'h23' }).format(
      instant,
    ),
  );
  return hour < dayStartHour ? addDays(day, -1) : day;
}

/** Today, on the årgang's clock. The strip's «i dag» means the festival's today, not the reader's. */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  return dayOfInstant(now.toISOString(), timeZone);
}

/**
 * Where the strip opens.
 *
 * ---------------------------------------------------------------------------------------------
 * THE BUILD DAYS, NOT THE FIRST GATE. A plan is opened to be worked on, and the work that is
 * behind is always the rigging — so the window starts `BUILD_LEAD` days before the årgang rather
 * than on its first day. Somebody who wants the festival itself is one press of › away, and the
 * strip marks the årgang's own days so the two are never confused.
 *
 * With no dates on the årgang at all there is nothing to lead into, and today is the honest
 * answer: it is the one day the reader certainly cares about.
 */
export const BUILD_LEAD = 3;

export function defaultWindowStart(
  startsOn: string | null,
  timeZone: string,
  now: Date = new Date(),
): string {
  if (startsOn === null) return addDays(todayIn(timeZone, now), -1);
  return addDays(startsOn, -BUILD_LEAD);
}

/**
 * The window as the two instants the API filters on.
 *
 * Midnight to midnight in the ÅRGANG's zone, which is why it is not `${day}T00:00:00Z`: a festival
 * in Oslo whose window was cut on UTC midnight would lose the last two hours of every night shift
 * in summer, and nobody would see the shifts go missing — they would just be missing.
 */
export function windowBounds(
  days: readonly string[],
  timeZone: string,
  /**
   * 250.13: festivaldøgnet. Vinduet forskyves med dagens start, så nattvakten etter midnatt på
   * vinduets siste dag er med, og den før grensen på første dag (gårsdagens) ikke er det.
   */
  dayStartHour = 0,
): { from: string; to: string } | null {
  const first = days[0];
  const last = days[days.length - 1];
  if (first === undefined || last === undefined) return null;
  const shifted = (iso: string): string =>
    iso === '' || dayStartHour === 0
      ? iso
      : new Date(Date.parse(iso) + dayStartHour * 3_600_000).toISOString();
  return {
    from: shifted(startOfDayInstant(first, timeZone)),
    to: shifted(startOfDayInstant(addDays(last, 1), timeZone)),
  };
}

/**
 * Midnight on `day` in `timeZone`, as an instant.
 *
 * Measured rather than assumed: the offset is read AT the candidate instant, so a window that
 * spans a daylight-saving change is still cut at local midnight on both sides. The one-step
 * correction is enough here because a window boundary is never inside a transition gap — midnight
 * is not an hour any jurisdiction skips.
 */
function startOfDayInstant(day: string, timeZone: string): string {
  const asIfUtc = Date.parse(`${day}T00:00:00Z`);
  /* `offsetMsAt` og `toISOString` kaster begge på NaN — en dag som ikke er en dag er tom (N1). */
  if (Number.isNaN(asIfUtc)) return '';
  const offset = offsetMsAt(new Date(asIfUtc), timeZone);
  return new Date(asIfUtc - offset).toISOString();
}

/** How far `timeZone` is ahead of UTC at one instant, in milliseconds. */
function offsetMsAt(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);

  const read = (type: string): number => Number(parts.find((part) => part.type === type)?.value);
  /* `hour12: false` can render midnight as 24 — normalise it, or the day is off by one. */
  const hour = read('hour') % 24;
  const asUtc = Date.UTC(
    read('year'),
    read('month') - 1,
    read('day'),
    hour,
    read('minute'),
    read('second'),
  );
  return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
}
